import React from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import Modal from '@/components/layout/Modal';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Botao from '@/components/Botao';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import Descricao from '@/components/tipografia/Descricao';
import { useAppliance } from '@/lib/useAppliance';
import { mdiDownload } from '@mdi/js';
import type { RadiusCertificadoDTO } from '@webip/shared/src/dto/radius_auth';

function baixar(pem: string, nomeArquivo: string) {
    const url = URL.createObjectURL(new Blob([pem], { type: 'application/x-x509-ca-cert' }));
    try {
        const a = document.createElement('a');
        a.href = url;
        a.download = nomeArquivo;
        document.body.appendChild(a);
        a.click();
        a.remove();
    } finally {
        URL.revokeObjectURL(url);
    }
}

function mensagemDeErro(err: unknown): string {
    if (axios.isAxiosError(err)) {
        return (err.response?.data as { msg?: string } | undefined)?.msg ?? err.message;
    }
    return 'Não foi possível obter o certificado.';
}

/**
 * Certificado autoassinado que o agente apresenta no PEAP. Cada agente gera o
 * seu, então a escolha da appliance importa; o dispositivo precisa confiar nele
 * ou recusa o handshake antes de enviar a senha.
 */
export default function CertificadoWifiModal({ show, onClose }: { show: boolean; onClose: () => void }) {
    const { applianceId, applianceOptions } = useAppliance();
    const [selecionada, setSelecionada] = React.useState<string | null>(null);
    const alvo = selecionada ?? applianceId ?? '';

    const query = useQuery(
        ['radius-certificado', alvo],
        async () => {
            const res = await axios.get<{ ok: boolean; result: RadiusCertificadoDTO }>(
                `/api/appliance/${alvo}/radius-certificado`,
                { withCredentials: true },
            );
            return res.data.result;
        },
        { enabled: show && !!alvo, retry: false, refetchOnWindowFocus: false },
    );

    const certificado = query.data;
    const nomeAppliance = applianceOptions.find((o) => o.id === alvo)?.label ?? alvo;
    const nomeArquivo = `webip-radius-${nomeAppliance.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}.crt`;

    return (
        <Modal show={show} onClose={onClose} title="Certificado Wi-Fi" size="lg" hideControls>
            <VBox style={{ gap: '12px' }}>
                <Descricao>
                    Instale este certificado nos dispositivos que recusam a conexão 802.1X. Ele é público:
                    a chave privada não sai do agente.
                </Descricao>

                <CampoSeletor
                    label="Appliance"
                    value={alvo}
                    onChange={(v) => setSelecionada(v ? String(v) : null)}
                    options={applianceOptions}
                />

                {query.isFetching && <Descricao>Consultando o agente…</Descricao>}
                {query.isError && !query.isFetching && (
                    <span style={{ color: 'var(--status-warn)' }}>{mensagemDeErro(query.error)}</span>
                )}

                {certificado && !query.isFetching && (
                    <VBox style={{ gap: '8px' }}>
                        <HBox style={{ gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ minWidth: '140px', color: 'var(--tc-color-text-muted, #999)' }}>Titular</span>
                            <span>{certificado.titular}</span>
                        </HBox>
                        <HBox style={{ gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ minWidth: '140px', color: 'var(--tc-color-text-muted, #999)' }}>Válido até</span>
                            <span>{new Date(certificado.valido_ate).toLocaleDateString('pt-BR')}</span>
                        </HBox>
                        <HBox style={{ gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ minWidth: '140px', color: 'var(--tc-color-text-muted, #999)' }}>SHA-256</span>
                            <span style={{ fontFamily: 'monospace', userSelect: 'all', overflowWrap: 'anywhere', flex: '1 1 240px' }}>
                                {certificado.fingerprint_sha256}
                            </span>
                        </HBox>
                        <div>
                            <Botao
                                variant="primary"
                                icon={mdiDownload}
                                onClick={() => baixar(certificado.pem, nomeArquivo)}
                            >
                                Baixar certificado
                            </Botao>
                        </div>
                    </VBox>
                )}

                <VBox style={{ gap: '4px' }}>
                    <strong>No Android</strong>
                    <ol style={{ margin: 0, paddingLeft: '20px' }}>
                        <li>Passe o arquivo para o dispositivo.</li>
                        <li>Em Configurações, procure "Instalar certificado" e escolha "Certificado de Wi-Fi".</li>
                        <li>Na rede, use EAP PEAP e autenticação de fase 2 MSCHAPV2.</li>
                        <li>Em "Certificado CA", selecione o certificado instalado e informe usuário e senha.</li>
                    </ol>
                </VBox>
            </VBox>
        </Modal>
    );
}
